import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

export function CreateItemDialog({ open, onOpenChange, onSuccess }) {
  const { t } = useTranslation();
  const mediaTypes = [
    { value: "video_game", label: t.mediaTypes.video_game },
    { value: "manga", label: t.mediaTypes.manga },
    { value: "comic", label: t.mediaTypes.comic },
    { value: "graphic_novel", label: t.mediaTypes.graphic_novel },
    { value: "book", label: t.mediaTypes.book },
    { value: "movie", label: t.mediaTypes.movie },
    { value: "tv_series", label: t.mediaTypes.tv_series },
  ];
  const [title, setTitle] = useState("");
  const [mediaType, setMediaType] = useState("video_game");
  const [creator, setCreator] = useState("");
  const [releaseYear, setReleaseYear] = useState("");
  const [description, setDescription] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setTitle("");
    setMediaType("video_game");
    setCreator("");
    setReleaseYear("");
    setDescription("");
    setCoverImage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error(t.createItem.titleRequired);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        media_type: mediaType,
        creator: creator.trim() || null,
        release_year: releaseYear ? parseInt(releaseYear) : null,
        description: description.trim() || null,
        cover_image: coverImage.trim() || null,
      };

      const response = await axios.post(`${API}/items`, payload);
      toast.success(t.createItem.createdSuccess(title));
      resetForm();
      onSuccess?.(response.data);
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating item:", error);
      toast.error(error.response?.data?.detail || t.createItem.createError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {t.createItem.title}
          </DialogTitle>
          <DialogDescription>
            {t.createItem.subtitle}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="title">{t.createItem.titleLabel}</Label>
              <Input
                id="title"
                placeholder={t.createItem.titlePlaceholder}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="item-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t.createItem.mediaType}</Label>
              <Select value={mediaType} onValueChange={setMediaType}>
                <SelectTrigger data-testid="item-media-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {mediaTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="releaseYear">{t.createItem.releaseYear}</Label>
              <Input
                id="releaseYear"
                type="number"
                placeholder="2024"
                min="1900"
                max="2030"
                value={releaseYear}
                onChange={(e) => setReleaseYear(e.target.value)}
                data-testid="item-year-input"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="creator">{t.createItem.creator}</Label>
              <Input
                id="creator"
                placeholder={t.createItem.creatorPlaceholder}
                value={creator}
                onChange={(e) => setCreator(e.target.value)}
                data-testid="item-creator-input"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="coverImage">{t.createItem.coverImage}</Label>
              <Input
                id="coverImage"
                type="url"
                placeholder="https://example.com/cover.jpg"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                data-testid="item-cover-input"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="description">{t.createItem.description}</Label>
              <Textarea
                id="description"
                placeholder={t.createItem.descriptionPlaceholder}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                data-testid="item-description-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t.common.cancel}
            </Button>
            <Button type="submit" disabled={loading} data-testid="create-item-submit">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t.createItem.creating}
                </>
              ) : (
                t.createItem.createButton
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
